import type { QualityRuleKey } from "@/hooks/useQualityConfig";

// Owner-facing copy for each quality rule. The admin panel uses its own
// rule-statement labels (RULE_META in QualityControlPanel); this is the
// gentler "here's how to fix it" voice shown to landlords on the banner and
// in the hide/recover emails.

export interface QualityRuleInfo {
  /** Short imperative label, e.g. "Add more photos". */
  label: string;
  /** One-line fix hint. */
  fix: string;
}

export const QUALITY_RULE_INFO: Record<QualityRuleKey, QualityRuleInfo> = {
  minPhotos: { label: "Add more photos", fix: "Upload a few more photos of the home." },
  descriptionExists: { label: "Add a description", fix: "Write a short description of the home." },
  descriptionNoNumbers: {
    label: "Remove numbers from the description",
    fix: "Move prices, sizes and phone numbers out of the description, they live in their own fields.",
  },
  descriptionCompliant: {
    label: "Improve the description",
    fix: "Revise the description to meet our standards, the “Revise by AI” button can do it for you.",
  },
  priceSet: { label: "Set a price", fix: "Add the monthly price." },
  gpsExifDistance: {
    label: "Fix the map pin",
    fix: "Move the map pin so it matches where your photos were taken.",
  },
  walkBeachDiff: {
    label: "Check the walk-to-beach time",
    fix: "Update the walking time to the beach so it matches the location.",
  },
  ownerHasAvatar: { label: "Add a profile photo", fix: "Add a profile photo to your account." },
  ownerHasBio: { label: "Add a profile bio", fix: "Write a short bio on your profile." },
  ownerResponseRate: {
    label: "Reply to your enquiries",
    fix: "Answer your pending requests: accept, decline, or reply in the chat.",
  },
  photosLabeled: { label: "Label your photos", fix: "Tag each photo with the room or area it shows." },
  essentialsAnswered: {
    label: "Answer the essentials",
    fix: "Tell renters whether the home has A/C, WiFi, a dishwasher, a washer and the rest of the ten essentials, a yes or a no for each, on the Amenities step.",
  },
};

/** The rule keys that concern the description (drive the "Revise by AI" CTA). */
export const DESCRIPTION_RULE_KEYS: QualityRuleKey[] = [
  "descriptionExists",
  "descriptionNoNumbers",
  "descriptionCompliant",
];

/** The admin's name for each rule (Admin -> Quality control and Admin -> Listings). */
export const QUALITY_RULE_ADMIN_LABELS: Record<QualityRuleKey, string> = {
  minPhotos: "Minimum photos",
  descriptionExists: "Description present",
  descriptionNoNumbers: "No numbers in description",
  descriptionCompliant: "Meets description standards (AI)",
  priceSet: "Price is set",
  gpsExifDistance: "Map pin matches photo GPS",
  walkBeachDiff: "Walk-to-beach is realistic",
  ownerHasAvatar: "Landlord has a profile photo",
  ownerHasBio: "Landlord has a bio",
  ownerResponseRate: "Landlord responds to requests",
  photosLabeled: "Photos are labeled",
  essentialsAnswered: "Essentials answered",
};

/**
 * Why a published listing is hidden from renters by quality control, as the
 * admin names the rules ("Minimum photos"), or null when it is not hidden
 * that way (Oct 15: Frank's Place was hidden yet showed as "Public").
 */
export function qualityHiddenReasons(p: { status: string; quality_visible?: boolean | null; quality_report?: unknown }): string[] | null {
  if (p.status !== "available" || p.quality_visible !== false) return null;
  const failed = (p.quality_report as { failed?: unknown } | null)?.failed;
  const keys = Array.isArray(failed) ? failed.filter((k): k is string => typeof k === "string") : [];
  return keys.map((k) => QUALITY_RULE_ADMIN_LABELS[k as QualityRuleKey] ?? k);
}
